import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Camera, Mic, Upload, BarChart3, Clock, FileText, Sparkles, FileBarChart, Type, Link2, Pencil, User, Images } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { isAIDisabled, setAIDisabled } from "@/lib/smartfile-ai-preference";

interface Patient {
  id: string;
  name: string;
  cpf: string;
  birth_date: string | null;
  email: string | null;
  phone: string | null;
  avatar_url?: string | null;
}

interface PatientPageHeaderProps {
  patient: Patient;
  onBack?: () => void;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onCameraClick?: () => void;
  onAudioClick?: () => void;
  onFileClick?: () => void;
  onTextClick?: () => void;
  isProcessing?: boolean;
  progressStage?: string;
  progressValue?: number;
}

const calculateAge = (birthDate: string | null) => {
  if (!birthDate) return "-";
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// Exportar tabs para uso em outros componentes
export const patientTabs = [
  { value: "indicators", label: "Indicadores", icon: BarChart3 },
  { value: "vision360", label: "Visão 360", icon: User },
  { value: "registros", label: "Timeline", icon: Clock },
  { value: "galeria", label: "Galeria", icon: Images },
  { value: "coleta", label: "Links", icon: Link2 },
  { value: "insights", label: "Análises IA", icon: Sparkles },
];

export function PatientPageHeader({
  patient,
  onBack,
  activeTab = "indicators",
  onTabChange,
  onCameraClick,
  onAudioClick,
  onFileClick,
  onTextClick,
  isProcessing = false,
  progressStage = "",
  progressValue = 0,
}: PatientPageHeaderProps) {
  const initials = patient.name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [disableAI, setDisableAI] = useState(() => isAIDisabled());

  // Salvar no localStorage sempre que mudar
  useEffect(() => {
    setAIDisabled(disableAI);
  }, [disableAI]);

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      {/* Primeira linha: Info do paciente + Botões */}
      <div className="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 w-full max-w-full">
        <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
          <Avatar className="h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0">
            {patient.avatar_url && <AvatarImage src={patient.avatar_url} alt={patient.name} />}
            <AvatarFallback className="bg-primary/10 text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
              <div className="flex flex-col gap-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold tracking-tight truncate">{patient.name}</h1>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/app/pacientes/${patient.id}/editar`)}
                    className="h-8 px-2 flex-shrink-0"
                    title="Editar paciente"
                  >
                    <Pencil className="h-4 w-4 mr-1.5" />
                    Editar
                  </Button>
                </div>

                <div className="flex flex-col gap-y-1 mt-1 text-xs sm:text-sm text-muted-foreground">
                  <span className="flex items-center gap-1 truncate">
                    <strong className="flex-shrink-0">CPF:</strong>
                    <span className="truncate">{patient.cpf}</span>
                  </span>
                  <span className="flex items-center gap-1 flex-shrink-0">
                    <strong>Idade:</strong> {calculateAge(patient.birth_date)} anos
                  </span>
                  {patient.phone && (
                    <span className="flex items-center gap-1 truncate">
                      <strong className="flex-shrink-0">Celular:</strong>
                      <span className="truncate">{patient.phone}</span>
                    </span>
                  )}
                  {patient.email && (
                    <span className="flex items-center gap-1 truncate">
                      <strong className="flex-shrink-0">E-mail:</strong>
                      <span className="truncate">{patient.email}</span>
                    </span>
                  )}
                </div>
              </div>

              {/* Botões SMARTFILE - Desktop */}
              <div className="hidden xl:flex flex-col gap-2 flex-shrink-0 min-w-0 border border-border/50 shadow-sm rounded-lg p-3 bg-card/50 transition-all duration-200">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex flex-col space-y-1.5 flex-1">
                    <div className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-primary" />
                      <span className="text-sm font-semibold">Captura Inteligente</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Insira os dados clínicos do paciente</p>
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id="disable-ai-header"
                      checked={disableAI}
                      onCheckedChange={(checked) => setDisableAI(checked === true)}
                    />
                    <Label
                      htmlFor="disable-ai-header"
                      className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
                    >
                      Desativar Resumo e Insights IA
                    </Label>
                  </div>
                </div>

                <div className="animate-in slide-in-from-top-2 duration-200">
                  <div className="flex gap-2">
                    <div className="flex-1 flex flex-col gap-1">
                      <Button
                        variant="default"
                        onClick={onCameraClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#camera`)}
                        className="bg-[#008ba3] hover:bg-[#007b91] text-white rounded-2xl shadow-sm h-9 px-4 w-full"
                        size="sm"
                        disabled={isProcessing}
                      >
                        <Camera className="h-3.5 w-3.5 mr-2" />
                        Câmera
                      </Button>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        Fotografe prontuários, anotações manuais
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <Button
                        variant="default"
                        onClick={onAudioClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#audio`)}
                        className="bg-[#008ba3] hover:bg-[#007b91] text-white rounded-2xl shadow-sm h-9 px-4 w-full"
                        size="sm"
                        disabled={isProcessing}
                      >
                        <Mic className="h-3.5 w-3.5 mr-2" />
                        Áudio
                      </Button>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        Dite observações clínicas
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <Button
                        variant="default"
                        onClick={onFileClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#upload`)}
                        className="bg-[#008ba3] hover:bg-[#007b91] text-white rounded-2xl shadow-sm h-9 px-4 w-full"
                        size="sm"
                        disabled={isProcessing}
                      >
                        <Upload className="h-3.5 w-3.5 mr-2" />
                        Arquivo
                      </Button>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        Suba PDFs, planilhas, resultados de exames
                      </span>
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      <Button
                        variant="default"
                        onClick={onTextClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#text`)}
                        className="bg-[#008ba3] hover:bg-[#007b91] text-white rounded-2xl shadow-sm h-9 px-4 w-full"
                        size="sm"
                        disabled={isProcessing}
                      >
                        <Type className="h-3.5 w-3.5 mr-2" />
                        Texto
                      </Button>
                      <span className="text-[10px] text-muted-foreground text-center leading-tight">
                        Anote atendimentos, evoluções, métricas de saúde
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Botões SMARTFILE - Mobile/Tablet */}
      <div className="flex xl:hidden flex-col gap-2 w-full mt-2 border border-border/50 shadow-sm rounded-lg p-3 bg-card/50 transition-all duration-200">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col space-y-1.5 flex-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold">Captura Inteligente</span>
            </div>
            <p className="text-xs text-muted-foreground">Insira os dados clínicos do paciente</p>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id="disable-ai-mobile"
              checked={disableAI}
              onCheckedChange={(checked) => setDisableAI(checked === true)}
            />
            <Label
              htmlFor="disable-ai-mobile"
              className="text-xs text-muted-foreground cursor-pointer whitespace-nowrap"
            >
              Desativar Resumo e Insights IA
            </Label>
          </div>
        </div>

        <div className="animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-2 gap-2 w-full">
            <div className="flex flex-col gap-1">
              <Button
                variant="outline"
                onClick={onCameraClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#camera`)}
                className="flex-shrink-0 bg-[#0891b2] text-white rounded-lg shadow-sm hover:bg-[#0891b2]/90 hover:text-white h-9 border-0 w-full"
                size="sm"
                disabled={isProcessing}
              >
                <Camera className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="ml-1.5 sm:ml-2 text-xs">Câmera</span>
              </Button>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                Fotografe prontuários, anotações manuais
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="outline"
                onClick={onAudioClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#audio`)}
                className="flex-shrink-0 bg-[#0891b2] text-white rounded-lg shadow-sm hover:bg-[#0891b2]/90 hover:text-white h-9 border-0 w-full"
                size="sm"
                disabled={isProcessing}
              >
                <Mic className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="ml-1.5 sm:ml-2 text-xs">Áudio</span>
              </Button>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                Dite observações clínicas
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="outline"
                onClick={onFileClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#upload`)}
                className="flex-shrink-0 bg-[#0891b2] text-white rounded-lg shadow-sm hover:bg-[#0891b2]/90 hover:text-white h-9 border-0 w-full"
                size="sm"
                disabled={isProcessing}
              >
                <Upload className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="ml-1.5 sm:ml-2 text-xs">Arquivo</span>
              </Button>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                Suba PDFs, planilhas, resultados de exames
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Button
                variant="outline"
                onClick={onTextClick || (() => window.location.href = `/app/smartfile?patientId=${patient.id}#text`)}
                className="flex-shrink-0 bg-[#0891b2] text-white rounded-lg shadow-sm hover:bg-[#0891b2]/90 hover:text-white h-9 border-0 w-full"
                size="sm"
                disabled={isProcessing}
              >
                <Type className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span className="ml-1.5 sm:ml-2 text-xs">Texto</span>
              </Button>
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                Anote atendimentos, evoluções, métricas de saúde
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de progresso - Exibida abaixo dos botões quando processando */}
      {isProcessing && progressStage && (
        <div className="w-full space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground font-medium">{progressStage}</span>
            <span className="text-muted-foreground">{progressValue}%</span>
          </div>
          <Progress value={progressValue} className="h-2" />
        </div>
      )}

      {/* Sistema de abas - Mobile: badges horizontais com scroll / Desktop: badges horizontais */}
      <div className="border-t pt-3 pb-2 w-full max-w-full overflow-x-hidden">
        {/* Mobile: Scroll horizontal com badges */}
        <div className="lg:hidden">
          <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:-mx-6 sm:px-6">
            {patientTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Badge
                  key={tab.value}
                  variant={activeTab === tab.value ? "default" : "outline"}
                  className={cn(
                    "shrink-0 px-3 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5",
                    activeTab === tab.value
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "hover:bg-accent"
                  )}
                  onClick={() => onTabChange?.(tab.value)}
                >
                  <Icon className="h-3.5 w-3.5" />
                  <span className="font-medium">{tab.label}</span>
                </Badge>
              );
            })}
          </div>
        </div>

        {/* Desktop: Badges horizontais */}
        <div className="hidden lg:flex gap-2 flex-wrap">
          {patientTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Badge
                key={tab.value}
                variant={activeTab === tab.value ? "default" : "outline"}
                className={cn(
                  "shrink-0 px-4 py-2 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-2",
                  activeTab === tab.value
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "hover:bg-accent"
                )}
                onClick={() => onTabChange?.(tab.value)}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </Badge>
            );
          })}
        </div>
      </div>
    </div>
  );
}
